import React, { useState, useEffect } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";

const COMPANIES = [
  { id: "ebek",    label: "Ebek",     accent: "#E31E24" },
  { id: "ebekmed", label: "Ebek Med", accent: "#2AB5A5" },
];

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN")}`;

export default function IssueAdvance() {
  const [company,    setCompany]    = useState("");
  const [employees,   setEmployees]   = useState([]);
  const [employeeId,  setEmployeeId]  = useState("");
  const [employee,    setEmployee]    = useState(null);
  const [amount,      setAmount]      = useState("");
  const [bankRef,     setBankRef]     = useState("");
  const [notes,       setNotes]       = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [history,     setHistory]     = useState([]);
  const [empCF,       setEmpCF]       = useState(0);       // employee carry-forward balance
  const [cfApply,     setCfApply]     = useState("");      // how much CF to apply this advance

  /* ── Load employees when company changes ── */
  useEffect(() => {
    if (!company) { setEmployees([]); setEmployeeId(""); setEmployee(null); setEmpCF(0); return; }
    API.get("/users")
      .then(r => {
        setEmployees(r.data.filter(u => u.company === company && u.role === "employee" && u.is_active !== false));
        setEmployeeId("");
        setEmployee(null);
        setEmpCF(0);
      })
      .catch(() => toast.error("Failed to load employees"));
  }, [company]);

  /* ── Set employee object + load CF balance when ID changes ── */
  useEffect(() => {
    const emp = employees.find(e => String(e.id) === String(employeeId)) || null;
    setEmployee(emp);
    setCfApply("");
    if (emp) {
      API.get(`/advances/carry-forward/${emp.id}`)
        .then(r => setEmpCF(parseFloat(r.data.carry_forward_balance || 0)))
        .catch(() => setEmpCF(0));
    } else {
      setEmpCF(0);
    }
  }, [employeeId, employees]);

  const co = COMPANIES.find(c => c.id === company);
  const accent = co?.accent || "#E31E24";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) return toast.error("Please select an employee");
    if (!amount || parseFloat(amount) <= 0) return toast.error("Enter a valid amount");

    const cfUsedAmt = parseFloat(cfApply || 0);
    if (cfUsedAmt > empCF) { toast.error(`Carry forward available is only ₹${empCF.toLocaleString("en-IN")}`); return; }

    setSubmitting(true);
    try {
      await API.post("/advances/direct", {
        employee_id       : employeeId,
        amount            : parseFloat(amount),
        bank_reference    : bankRef || undefined,
        notes             : notes   || undefined,
        carry_forward_used: cfUsedAmt > 0 ? cfUsedAmt : undefined,
      });

      toast.success(`₹${parseFloat(amount).toLocaleString("en-IN")} issued to ${employee?.name?.split(" ")[0]}!`);

      // Add to session history
      setHistory(prev => [{
        employee : employee?.name,
        company  : co?.label,
        amount   : parseFloat(amount),
        bankRef,
        time     : new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
      }, ...prev].slice(0, 10));

      // Reset amount + refs but keep company + employee for quick repeat
      setAmount("");
      setBankRef("");
      setNotes("");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to issue advance");
    } finally {
      setSubmitting(false);
    }
  };

  const readyToSubmit = employeeId && (parseFloat(amount) > 0 || parseFloat(cfApply) > 0);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title">💸 Issue Advance</h1>
        <p className="page-subtitle">Transfer advance directly to an employee's kitty — no trip required</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24, alignItems: "start" }}>

        {/* ── FORM ── */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Company */}
          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>
              Company
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              {COMPANIES.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCompany(c.id)}
                  style={{
                    flex: 1, padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                    border: company === c.id ? `2px solid ${c.accent}` : "2px solid var(--border)",
                    background: company === c.id ? `${c.accent}14` : "var(--bg-surface)",
                    display: "flex", alignItems: "center", gap: 10, transition: "all 0.18s",
                  }}
                >
                  <span style={{
                    width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                    background: company === c.id ? c.accent : "#d1d5db",
                  }} />
                  <span style={{ fontWeight: 700, fontSize: 14, color: company === c.id ? c.accent : "var(--text-muted)" }}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Employee */}
          {company && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>
                Employee
              </div>

              {employees.length === 0 ? (
                <div style={{ color: "#9ca3af", fontSize: 14, textAlign: "center", padding: "16px 0" }}>
                  No active employees in {co?.label}
                </div>
              ) : (
                <select
                  className="form-control"
                  value={employeeId}
                  onChange={e => setEmployeeId(e.target.value)}
                >
                  <option value="">— Select employee —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}{emp.department ? `  ·  ${emp.department}` : ""}
                    </option>
                  ))}
                </select>
              )}

              {/* Employee card */}
              {employee && (
                <div style={{
                  marginTop: 14, padding: "14px 16px", borderRadius: 10,
                  background: `${accent}08`, border: `1.5px solid ${accent}30`,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)", marginBottom: 8 }}>
                    {employee.name}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 13, color: "var(--text-muted)" }}>
                    <span>📧 {employee.email}</span>
                    {employee.department && <span>🏢 {employee.department}</span>}
                    {employee.phone && <span>📱 {employee.phone}</span>}
                    {employee.bank_account && (
                      <span style={{ gridColumn: "1/-1", color: "var(--text-secondary)", fontWeight: 600 }}>
                        🏦 A/C: {employee.bank_account}
                        {employee.bank_name ? `  ·  ${employee.bank_name}` : ""}
                        {employee.ifsc_code ? `  ·  IFSC: ${employee.ifsc_code}` : ""}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Carry Forward Balance Alert */}
          {employeeId && empCF > 0 && (
            <div style={{ background: "linear-gradient(135deg,#065F46,#047857)", borderRadius: 14, padding: "16px 20px", color: "white" }}>
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>💰 Carry Forward Balance Available</div>
              <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>₹{empCF.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 14 }}>
                {employee?.name} has an existing carry-forward balance. You can apply all or part of it towards this advance. Only the remaining amount will be transferred via bank.
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 6 }}>
                  Apply Carry Forward (₹) — max ₹{empCF.toLocaleString("en-IN")}
                </label>
                <input
                  type="number" min="0" step="0.01" max={empCF}
                  placeholder="0 — leave blank to not apply"
                  value={cfApply}
                  onChange={e => setCfApply(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "none", background: "rgba(255,255,255,0.15)", color: "white", fontSize: 16, fontWeight: 700, outline: "none", boxSizing: "border-box" }}
                />
              </div>
              {parseFloat(cfApply || 0) > 0 && parseFloat(amount || 0) >= 0 && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.15)", borderRadius: 10, fontSize: 13 }}>
                  <div>Total Advance: ₹{(parseFloat(amount||0)+parseFloat(cfApply||0)).toLocaleString("en-IN")}</div>
                  <div>Bank Transfer (new cash): ₹{parseFloat(amount||0).toLocaleString("en-IN")}</div>
                  <div>From Carry Forward: ₹{parseFloat(cfApply).toLocaleString("en-IN")}</div>
                  <div style={{ marginTop:4, opacity:0.8 }}>Remaining CF after this: ₹{Math.max(0, empCF - parseFloat(cfApply||0)).toLocaleString("en-IN")}</div>
                </div>
              )}
            </div>
          )}

          {/* Advance details */}
          {employeeId && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>
                {empCF > 0 ? "New Cash Transfer Amount (₹)" : "Advance Details"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{empCF > 0 ? "New Cash Transfer (₹) *" : "Amount (₹) *"}</label>
                  <input
                    className="form-control"
                    type="number" min="0" step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    required={parseFloat(cfApply||0) === 0}
                    autoFocus
                    style={{ fontSize: 20, fontWeight: 800, color: accent }}
                  />
                  {empCF > 0 && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Enter 0 if fully covered by carry forward</div>}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Bank Ref / UTR No.</label>
                  <input
                    className="form-control"
                    placeholder="Transfer reference"
                    value={bankRef}
                    onChange={e => setBankRef(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: "1/-1", marginBottom: 0 }}>
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    placeholder="Any note for the employee..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          {readyToSubmit && (
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: "15px 24px", fontSize: 16, fontWeight: 800, borderRadius: 12,
                background: submitting ? "#9ca3af" : accent,
                color: "white", border: "none",
                cursor: submitting ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: submitting ? "none" : `0 4px 16px ${accent}44`,
                transition: "all 0.2s",
              }}
            >
              {submitting
                ? "Issuing..."
                : `💸 Issue ${fmt(amount)} to ${employee?.name?.split(" ")[0]}`}
            </button>
          )}
        </form>

        {/* ── RIGHT PANEL ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 90 }}>

          {/* Live summary */}
          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 16 }}>
              Summary
            </div>
            {!company ? (
              <p style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: "16px 0", margin: 0 }}>
                Select a company to begin
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Row label="Company"  value={co?.label} color={accent} />
                {employee  && <Row label="Employee"  value={employee.name} />}
                {employee?.department && <Row label="Dept" value={employee.department} />}
                {bankRef   && <Row label="Bank Ref"  value={bankRef} />}

                {parseFloat(amount) > 0 && (
                  <div style={{
                    marginTop: 8, padding: "18px 14px", borderRadius: 12,
                    background: `${accent}12`, border: `2px solid ${accent}30`,
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Advance Amount
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 900, color: accent, marginTop: 4 }}>
                      {fmt(amount)}
                    </div>
                    {employee && (
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
                        → {employee.name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Session history */}
          {history.length > 0 && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>
                Issued This Session
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {history.map((h, i) => (
                  <div key={i} style={{
                    padding: "10px 12px", borderRadius: 8,
                    background: "var(--bg-surface)", border: "1px solid var(--border)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{h.employee}</span>
                      <span style={{ fontWeight: 800, fontSize: 13, color: "#059669" }}>{fmt(h.amount)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                      {h.company} · {h.time}
                      {h.bankRef && ` · Ref: ${h.bankRef}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || "var(--text-primary)", textAlign: "right" }}>{value}</span>
    </div>
  );
}
